/**
 * Everything the app needs before the first render, parsed and validated in one place:
 * the host-provided config (`dynamicEnv`: query string -> window.inlineConfig -> build-time VITE_*)
 * and the startup query params.
 */
import {
  isOfflineViewMode,
  isOnlineViewMode,
  OfflineStudentViewModes,
  OnlineStudentViewModes,
  StudentViewMode,
  studentViewModeSchema,
} from './constants';
import { dynamicEnv } from './dynamic/dynamicEnv';
import * as z from 'zod/mini';

/**
 * `?flag=true|1|yes|on` enables, `?flag=false|0|no|off` disables. Missing, empty (`?flag`) or an
 * unrecognized value counts as disabled — note this differs from the old `Boolean(param)` parsing,
 * which treated `?flag=false` as enabled.
 */
const queryFlagSchema = z.catch(z.stringbool(), false);

/** Locale tags like `en` or `en-us`; anything else is ignored so the project culture wins. */
const cultureCodeSchema = z.catch(
  z.nullable(z.string().check(z.trim(), z.toLowerCase(), z.regex(/^[a-z]{2}(-[a-z]{2})?$/))),
  null
);

const startupOptionsSchema = z.pipe(
  z.object({
    viewMode: studentViewModeSchema,
    backendUrl: z.pipe(
      z.string({ error: 'BACKEND_URL is not set.' }).check(z.trim(), z.minLength(1, 'BACKEND_URL is empty.')),
      z.transform((url: string) => url.replace(/\/+$/, ''))
    ),
    cultureCode: cultureCodeSchema,
    isInPreview: queryFlagSchema,
    hideStudentHeader: queryFlagSchema,
    showiOSSettingButton: queryFlagSchema,
    enableKrpanoDebugging: queryFlagSchema,
    hideLoader: queryFlagSchema,
  }),
  // An exported package has no host application around it: there is no preview to be in and no
  // student header to hide, so those two query params are ignored offline.
  z.transform((options) =>
    isOfflineViewMode(options.viewMode) ? { ...options, isInPreview: false, hideStudentHeader: false } : options
  )
);

/**
 * `TViewMode` lets a consumer state which family of view modes it handles — narrow an unknown
 * `StartupOptions` with {@link isOnlineStartup} / {@link isOfflineStartup} to get there.
 */
export type StartupOptions<TViewMode extends StudentViewMode = StudentViewMode> = Omit<
  z.infer<typeof startupOptionsSchema>,
  'viewMode'
> & { viewMode: TViewMode };

export const isOnlineStartup = (options: StartupOptions): options is StartupOptions<OnlineStudentViewModes> =>
  isOnlineViewMode(options.viewMode);

export const isOfflineStartup = (options: StartupOptions): options is StartupOptions<OfflineStudentViewModes> =>
  isOfflineViewMode(options.viewMode);

/**
 * @throws when `VIEW_MODE` or `BACKEND_URL` is missing or unknown — the app cannot start without
 * them, so the error surfaces through the root error boundary.
 */
export const readStartupOptions = (search: string): StartupOptions => {
  const params = new URLSearchParams(search);
  const result = startupOptionsSchema.safeParse({
    viewMode: dynamicEnv('VIEW_MODE'),
    backendUrl: dynamicEnv('BACKEND_URL'),
    cultureCode: params.get('cultureCode'),
    isInPreview: params.get('preview'),
    hideStudentHeader: params.get('hideStudentHeader'),
    showiOSSettingButton: params.get('showiOSSettingButton'),
    enableKrpanoDebugging: params.get('krpanoDebug'),
    hideLoader: params.get('hideLoader'),
  });

  if (!result.success) {
    throw new Error(`Cannot start the student app, invalid startup configuration:\n${z.prettifyError(result.error)}`);
  }

  return result.data;
};
